import React, { ReactElement, useLayoutEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { makeStyles, Tooltip } from '@material-ui/core'

import {
  getTooltipBoxTooltipClass,
  useTooltipBoxStyles,
} from '@objects/tooltipBox'
import GermanyMapSvg from '@static/img/map/germany-bundeslaender.inline.svg'
import {
  AccidentAtlasBundeslandId,
  getRegionName,
  isBundeslandRegionId,
} from '../types/region'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 369,
    '& svg': {
      width: '100%',
      height: 'auto',
      display: 'block',
    },
    '& .bundesland': {
      fill: theme.palette.primary.main,
      stroke: '#FFFFFF',
      strokeWidth: 1,
      vectorEffect: 'non-scaling-stroke',
      cursor: 'pointer',
      transition: 'fill 0.15s ease',
      '&:hover': {
        fill: theme.palette.primary.lightest,
      },
    },
    '& .bundeslandSelected': {
      fill: theme.palette.primary.lightest,
      '&:hover': {
        fill: theme.palette.primary.lightest,
      },
    },
  },
  tooltipAnchor: {
    position: 'fixed',
    width: 0,
    height: 0,
    pointerEvents: 'none',
  },
}))

export type GermanyMapProps = RvG.IReactDefaultProps & {
  selectedId?: AccidentAtlasBundeslandId
  onSelect?: (id: AccidentAtlasBundeslandId) => void
}

type HoveredRegion = {
  id: AccidentAtlasBundeslandId
  anchorEl: SVGPathElement
}

export default function GermanyMap({
  className,
  selectedId,
  onSelect,
}: GermanyMapProps): ReactElement {
  const classes = useStyles()
  const tooltipBoxClasses = useTooltipBoxStyles()
  const mapRef = useRef<HTMLDivElement>(null)
  const [hoveredRegion, setHoveredRegion] = useState<HoveredRegion | null>(null)

  useLayoutEffect(() => {
    const root = mapRef.current
    if (!root) return undefined

    const paths = root.querySelectorAll<SVGPathElement>('path[id]')
    const cleanups: Array<() => void> = []

    paths.forEach((path) => {
      const regionId = path.id
      if (!isBundeslandRegionId(regionId)) return

      path.classList.toggle('bundeslandSelected', regionId === selectedId)

      const bundeslandId: AccidentAtlasBundeslandId = regionId

      function handlePointerEnter(): void {
        setHoveredRegion({ id: bundeslandId, anchorEl: path })
      }

      function handlePointerLeave(): void {
        setHoveredRegion((current) =>
          current?.anchorEl === path ? null : current
        )
      }

      path.addEventListener('mouseenter', handlePointerEnter)
      path.addEventListener('mouseleave', handlePointerLeave)

      cleanups.push(() => {
        path.removeEventListener('mouseenter', handlePointerEnter)
        path.removeEventListener('mouseleave', handlePointerLeave)
      })
    })

    return () => {
      cleanups.forEach((cleanup) => cleanup())
    }
  }, [selectedId])

  function handleClick(event: React.MouseEvent<HTMLDivElement>): void {
    if (!onSelect) return

    const target = event.target
    if (!(target instanceof SVGPathElement)) return

    const id = target.id
    if (isBundeslandRegionId(id)) {
      onSelect(id)
    }
  }

  const tooltipTitle = hoveredRegion
    ? getRegionName(hoveredRegion.id) ?? ''
    : ''

  return (
    <>
      <div
        ref={mapRef}
        className={clsx(classes.root, className)}
        onClick={handleClick}
        role={onSelect ? 'img' : undefined}
        aria-label={
          onSelect
            ? 'Deutschlandkarte: Bundesland per Maus auswählen'
            : 'Deutschlandkarte mit Bundesländern'
        }
      >
        <GermanyMapSvg />
      </div>
      <Tooltip
        open={Boolean(hoveredRegion)}
        title={tooltipTitle}
        classes={{
          tooltip: getTooltipBoxTooltipClass(tooltipBoxClasses, 'dark'),
        }}
        PopperProps={{
          anchorEl: hoveredRegion?.anchorEl ?? null,
        }}
      >
        <span className={classes.tooltipAnchor} aria-hidden />
      </Tooltip>
    </>
  )
}
